import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, Loader2, Trash2, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import { useReveal } from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nb-admin-token";

export default function AdminContactsPage() {
  useReveal();
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);

  const fetchContacts = async (tk) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setContacts(res.data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, tk);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("Invalid token");
        setAuthed(false);
        localStorage.removeItem(STORAGE_KEY);
      } else {
        setError(err?.message || "Could not load contacts");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchContacts(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setToken(input);
    fetchContacts(input);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts((c) => c.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setAuthed(false);
    setContacts([]);
  };

  if (!authed) {
    return (
      <main className="min-h-[70vh] max-w-md mx-auto px-6 pt-40 pb-24">
        <Link
          to="/"
          className="reveal inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.22em] text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft size={12} /> Home
        </Link>
        <div className="reveal mt-6 flex items-center gap-3">
          <Lock size={16} className="text-[#38BDF8]" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#38BDF8]">
            Admin Access
          </span>
        </div>
        <h1 className="reveal mt-5 font-display text-white text-[40px] leading-tight font-medium">
          Contact submissions
        </h1>
        <p className="reveal mt-3 text-[#94A3B8] text-[14px]">
          Enter the admin token to view inbound research, clinical, and investment requests.
        </p>
        <form onSubmit={submit} className="reveal mt-8 space-y-4">
          <input
            data-testid="admin-token-input"
            type="password"
            placeholder="Admin token"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full bg-[#0B1426] border border-white/10 focus:border-[#38BDF8] rounded-xl px-4 py-3 text-white outline-none text-[14px]"
          />
          {error && (
            <div className="text-[13px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading || !input}
            className="btn-glow w-full inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-white text-[#020617] text-[14px] font-semibold hover:bg-[#BFDBFE] disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            Unlock
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="max-w-[1180px] mx-auto px-6 lg:px-12 pt-28 md:pt-32 pb-24">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.22em] text-[#94A3B8] hover:text-white"
          >
            <ArrowLeft size={12} /> Home
          </Link>
          <h1 className="mt-4 font-display text-white text-[clamp(28px,4vw,44px)] font-medium tracking-tight">
            Contact submissions{" "}
            <span className="text-[#38BDF8] font-mono text-[16px]">
              {contacts.length}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-refresh"
            onClick={() => fetchContacts(token)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white text-[13px] hover:border-[#38BDF8]/50"
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Refresh
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[#94A3B8] text-[13px] hover:text-white"
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="mt-10 space-y-3">
        {contacts.length === 0 && !loading && (
          <div className="glass rounded-2xl p-10 text-center text-[#94A3B8] text-[14px]">
            No submissions yet.
          </div>
        )}
        {contacts.map((c) => (
          <article
            key={c.id}
            data-testid={`admin-contact-${c.id}`}
            className="glass rounded-2xl p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start"
          >
            <div className="lg:col-span-3">
              <div className="font-display text-white text-[17px] font-medium leading-snug">
                {c.name}
              </div>
              {c.organization && (
                <div className="mt-1 text-[12.5px] text-[#94A3B8]">
                  {c.organization}
                </div>
              )}
              <a
                href={`mailto:${c.email}`}
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-[#38BDF8] hover:underline"
              >
                <Mail size={12} />
                {c.email}
              </a>
            </div>
            <div className="lg:col-span-2">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#475569]">
                Interest
              </div>
              <div className="mt-1.5 inline-flex px-2.5 py-1 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/30 text-[#BFDBFE] text-[11.5px] uppercase tracking-wider">
                {c.interest}
              </div>
              <div className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[#475569]">
                {new Date(c.created_at).toLocaleString()}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#475569]">
                Message
              </div>
              <p className="mt-1.5 text-[13.5px] text-[#CBD5E1] whitespace-pre-wrap leading-relaxed">
                {c.message}
              </p>
            </div>
            <div className="lg:col-span-1 flex lg:justify-end">
              <button
                data-testid={`admin-delete-${c.id}`}
                onClick={() => remove(c.id)}
                aria-label="Delete"
                className="h-9 w-9 grid place-items-center rounded-full border border-white/10 text-[#94A3B8] hover:text-red-300 hover:border-red-500/50"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}
